import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import useFetchDatav2 from '../hooks/useFetchDatav2';

const schema = yup.object({
  name: yup.string().required('Name is required'),
  displayText: yup.string().required('Display text is required'),
  selectedModules: yup
    .array()
    .min(1, 'Select at least one module')
    .required('Select at least one module'),
  completionThreshold: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Min 0')
    .max(1, 'Max 1')
    .default(0.8),
  duration: yup.string(),
  isCore: yup.boolean().default(false),
  sortOrder: yup
    .number()
    .typeError('Must be a number')
    .integer('Must be an integer')
    .default(0),
});

const btnSx = {
  backgroundColor: '#7b2d4e',
  '&:hover': { backgroundColor: '#5e2139' },
};

function SpecializationTrackConfig({ cohortId }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingTrackId, setDeletingTrackId] = useState(null);

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      displayText: '',
      selectedModules: [],
      duration: '',
      completionThreshold: 0.8,
      isCore: false,
      sortOrder: 0,
    },
  });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTrack(null);
    reset();
  };

  const { data: cohortData } = useFetchDatav2(
    ['cohort', cohortId],
    `/cohorts/${cohortId}`,
    {},
    null,
    !!cohortId
  );

  const cohort = cohortData?.data?.cohort || cohortData?.data || {};
  const courseId = cohort.course_id || '';
  const cohortModuleIds = (() => {
    try {
      const raw = cohort.modules;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') return JSON.parse(raw);
      return [];
    } catch {
      return [];
    }
  })();

  const { data: courseModulesData } = useFetchDatav2(
    ['course-modules', courseId],
    '/course-modules',
    { params: { courseId, is_paid_module: false } },
    null,
    !!courseId
  );

  const allCourseModules = courseModulesData?.data?.course_modules || [];

  const cohortModules =
    cohortModuleIds.length > 0
      ? allCourseModules.filter((m) => cohortModuleIds.includes(m.id))
      : allCourseModules;

  const moduleOptions = cohortModules.map((m) => ({
    value: m.id,
    label: m.moduleName,
  }));

  const { data: tracksData, isLoading: isLoadingTracks } = useFetchDatav2(
    ['specialization-tracks', cohortId],
    `/cohorts/${cohortId}/specialization-tracks`,
    {},
    'Could not fetch specialization tracks',
    !!cohortId
  );

  const tracks = tracksData?.data?.tracks || tracksData?.data || [];

  const moduleNameMap = {};
  allCourseModules.forEach((m) => {
    moduleNameMap[m.id] = m.moduleName;
  });

  const createMutation = useMutation(
    (data) =>
      axiosInstancev2.post(`/cohorts/${cohortId}/specialization-tracks`, data),
    {
      onSuccess: () => {
        toast.success('Track created successfully');
        queryClient.invalidateQueries(['specialization-tracks', cohortId]);
        handleCloseDialog();
      },
      onError: (error) => {
        toast.error(
          `Failed to create track. ${error?.response?.data?.error || ''}`
        );
      },
    }
  );

  const updateMutation = useMutation(
    ({ trackId, data }) =>
      axiosInstancev2.put(`/specialization-tracks/${trackId}`, data),
    {
      onSuccess: () => {
        toast.success('Track updated successfully');
        queryClient.invalidateQueries(['specialization-tracks', cohortId]);
        handleCloseDialog();
      },
      onError: (error) => {
        toast.error(
          `Failed to update track. ${error?.response?.data?.error || ''}`
        );
      },
    }
  );

  const deleteMutation = useMutation(
    (trackId) => axiosInstancev2.delete(`/specialization-tracks/${trackId}`),
    {
      onSuccess: () => {
        toast.success('Track deleted successfully');
        queryClient.invalidateQueries(['specialization-tracks', cohortId]);
        setDeleteConfirmOpen(false);
        setDeletingTrackId(null);
      },
      onError: (error) => {
        toast.error(
          `Failed to delete track. ${error?.response?.data?.error || ''}`
        );
      },
    }
  );

  const handleOpenCreate = () => {
    setEditingTrack(null);
    reset({
      name: '',
      displayText: '',
      selectedModules: [],
      duration: '',
      completionThreshold: 0.8,
      isCore: false,
      sortOrder: Array.isArray(tracks) ? tracks.length : 0,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (track) => {
    setEditingTrack(track);
    const trackModuleIds = track.required_module_ids || [];
    const selectedOptions = trackModuleIds
      .map((id) => {
        const mod = allCourseModules.find((m) => m.id === id);
        return mod ? { value: mod.id, label: mod.moduleName } : null;
      })
      .filter(Boolean);

    reset({
      name: track.name || '',
      displayText: track.display_text || '',
      selectedModules: selectedOptions,
      duration: track.duration || '',
      completionThreshold: track.completion_threshold ?? 0.8,
      isCore: track.is_core || false,
      sortOrder: track.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const handleConfirmDelete = (trackId) => {
    setDeletingTrackId(trackId);
    setDeleteConfirmOpen(true);
  };

  const handleModuleChange = (selected) => {
    setValue('selectedModules', selected || [], { shouldValidate: true });

    const selectedIds = (selected || []).map((s) => s.value);
    const selectedMods = allCourseModules.filter((m) =>
      selectedIds.includes(m.id)
    );

    const durations = selectedMods
      .map((m) => m.hours_of_learning)
      .filter(Boolean);
    if (durations.length === 1) {
      setValue('duration', durations[0]);
    } else if (durations.length > 1) {
      setValue('duration', durations.join(' + '));
    } else {
      setValue('duration', '');
    }

    if (!watch('name') || !editingTrack) {
      const names = (selected || []).map((s) => s.label);
      const snakeName = names
        .join('_and_')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 120);
      const displayText = names.join(', ').slice(0, 250);

      if (!editingTrack) {
        setValue('name', snakeName);
        setValue('displayText', displayText);
      }
    }
  };

  const onSubmit = (data) => {
    const moduleIdsArray = (data.selectedModules || []).map((m) => m.value);

    const payload = {
      name: data.name,
      display_text: data.displayText,
      required_module_ids: moduleIdsArray,
      duration: data.duration || '',
      completion_threshold: data.completionThreshold,
      is_core: data.isCore,
      sort_order: data.sortOrder,
    };

    if (editingTrack) {
      updateMutation.mutate({ trackId: editingTrack.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-700">
            Specialization Tracks
          </h3>
          {cohortModules.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {cohortModules.length} module
              {cohortModules.length !== 1 ? 's' : ''} available from{' '}
              {cohort.name || 'this cohort'}
            </p>
          )}
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={btnSx}
        >
          Add Track
        </Button>
      </div>

      {isLoadingTracks ? (
        <div className="flex justify-center py-8">
          <CircularProgress />
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Display Text</strong>
                </TableCell>
                <TableCell>
                  <strong>Modules</strong>
                </TableCell>
                <TableCell>
                  <strong>Duration</strong>
                </TableCell>
                <TableCell>
                  <strong>Core?</strong>
                </TableCell>
                <TableCell>
                  <strong>Threshold</strong>
                </TableCell>
                <TableCell>
                  <strong>Sort</strong>
                </TableCell>
                <TableCell>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(tracks) && tracks.length > 0 ? (
                tracks.map((track) => (
                  <TableRow key={track.id}>
                    <TableCell>{track.name}</TableCell>
                    <TableCell>{track.display_text}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(track.required_module_ids || []).map((modId) => (
                          <Chip
                            key={modId}
                            label={moduleNameMap[modId] || modId.slice(0, 8)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{track.duration || '-'}</TableCell>
                    <TableCell>{track.is_core ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{track.completion_threshold}</TableCell>
                    <TableCell>{track.sort_order}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(track)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleConfirmDelete(track.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No specialization tracks configured
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingTrack ? 'Edit Track' : 'Add Track'}</DialogTitle>
        <DialogContent>
          <form
            id="track-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 mt-2"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Modules for this Track
              </label>
              <Controller
                name="selectedModules"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    isClearable
                    className="react-dropdown"
                    classNamePrefix="dropdown"
                    options={moduleOptions}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: 'white',
                      }),
                    }}
                    placeholder="Pick modules from this cohort..."
                    noOptionsMessage={() =>
                      courseId
                        ? 'No modules found for this course'
                        : 'Loading course data...'
                    }
                    onChange={(selected) => {
                      field.onChange(selected);
                      handleModuleChange(selected);
                    }}
                  />
                )}
              />
              {errors.selectedModules && (
                <span className="text-red-600 text-xs mt-1">
                  {errors.selectedModules?.message}
                </span>
              )}
            </div>
            <TextField
              label="Track Name (internal)"
              fullWidth
              size="small"
              {...register('name')}
              error={!!errors.name}
              helperText={
                errors.name?.message || 'Auto-generated from selected modules'
              }
            />
            <TextField
              label="Display Text (shown on certificate)"
              fullWidth
              size="small"
              {...register('displayText')}
              error={!!errors.displayText}
              helperText={
                errors.displayText?.message ||
                'This text appears on the certificate'
              }
            />
            <TextField
              label="Duration"
              fullWidth
              size="small"
              {...register('duration')}
              helperText="Auto-filled from module hours of learning"
            />
            <TextField
              label="Completion Threshold"
              fullWidth
              size="small"
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 1 }}
              {...register('completionThreshold')}
              error={!!errors.completionThreshold}
              helperText={
                errors.completionThreshold?.message ||
                '0.8 = student needs progress in 80% of modules to qualify'
              }
            />
            <Controller
              name="isCore"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Core Track (included for all students, not a specialization)"
                />
              )}
            />
            <TextField
              label="Sort Order"
              fullWidth
              size="small"
              type="number"
              {...register('sortOrder')}
              error={!!errors.sortOrder}
              helperText={errors.sortOrder?.message}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="track-form"
            variant="contained"
            disabled={isSubmitting}
            sx={btnSx}
          >
            {(() => {
              if (isSubmitting)
                return <CircularProgress size={20} color="inherit" />;
              if (editingTrack) return 'Update';
              return 'Create';
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this specialization track?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isLoading}
            onClick={() => deleteMutation.mutate(deletingTrackId)}
          >
            {deleteMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SpecializationTrackConfig;
